import {Inject, Injectable} from 'ng-metadata/core';
import {
    getAttributesMap,
    getBackendSystem,
    getInstitutionCode,
    getPrimoVeSkipDeliveryForCollectionDiscovery,
    getVid
} from "../appConfig/appConfigForward";
import {Cheetah} from "../search/cheetah.service";
import {PrimoResponse} from "../infra/dataStructures/PrimoResponses/doc.interfcae";
import {MappingTablesCache} from "../mappingTablesCache/mappingTablesCache.service";
import {ResultUtil} from "../nddUtils/resultUtil.service";
import {
    HandleLimitOffsetErrorResult,
    RestrictedOffsetHandlerService
} from "../security/restrictedOffsetHandler.service";
import {SearchStateService} from "../search/searchState.service";

const get = require('lodash/get');
const find = require('lodash/find');
const chunk = require('lodash/chunk');
const max = require('lodash/max');

@Injectable()
export class CollectionDiscoveryService {
    private restIlsUrl: string;
    private breadcrumbs: Object;
    private collectionTreePromiseMap: Object = {};
    private sourceCodeIdPromiseMap: Object = {};
    private _itemsViewStyle: string;
    public searchInProgress: boolean;
    private _isCollectionDiscoverySpecificDisplay: boolean;

    constructor(@Inject('$http') private $http: ng.IHttpService,
                @Inject('restBaseURLs') restBaseURLs,
                @Inject('$stateParams') private $stateParams: ng.ui.IStateParamsService,
                @Inject('$q') private $q: ng.IQService,
                @Inject('$translate') private $translate: ng.translate.ITranslateService,
                private searchStateService: SearchStateService,
                private cheetah: Cheetah,
                private mappingTablesCache: MappingTablesCache,
                private restrictedOffsetHandlerService: RestrictedOffsetHandlerService,
                private resultUtil: ResultUtil) {
        this.$http = $http;
        this.restIlsUrl = restBaseURLs.myAccountBaseURL + "/collection";
        this._itemsViewStyle = 'grid';
        if (getBackendSystem() === 'suprima') {
            this._isCollectionDiscoverySpecificDisplay = getAttributesMap().collectionDiscoveryEnabled;
        } else {
            this._isCollectionDiscoverySpecificDisplay =
                this.mappingTablesCache.findByProperties('Collection Discovery Configuration', {
                    source1: 'Use Collection-specific display',
                    target: 'Yes'
                }).length > 0;
        }
    }

    getCollectionTreePromise(inst ?: string, lang ?: string): Promise<[ICDCollectionObject]> {
        let institution = inst || getInstitutionCode();
        let language = lang || this.$translate.use();
        let collectionTreeKey = institution + '_' + language;
        return this.collectionTreePromiseMap[collectionTreeKey];
    }


    getBreadcrumbs() {
        return this.breadcrumbs;
    }

    public getCollection(inst ?: string, lang ?: string) {
        let institution = inst || getInstitutionCode();
        let language = lang || this.$translate.use();
        let collectionTreeKey = institution + '_' + language;
        if (this.collectionTreePromiseMap[collectionTreeKey]) {
            return this.collectionTreePromiseMap[collectionTreeKey];
        }
        let dataToBeSent = {
            inst: institution,
            vid: getVid(),
			name: 'get_collections',
            lang: this.$translate.use(),
            userInst: getInstitutionCode(),
            ilsgEntityParams: 'primo_user_institution,' + getInstitutionCode() + ';ils_user_institution,' + institution,
            useCache: true
        };
        let promise = this.$http.get(this.restIlsUrl, {
            params: dataToBeSent
        }).then((data) => {
            return get(data, 'data.data.collection', undefined);
        });
        this.collectionTreePromiseMap[collectionTreeKey] = promise;
        return promise;
    }

    getSubColWithPath(pid: string, inst ?: string): Promise<{ level: number, path: [ICollectionPathObject], value: ICDCollectionObject }> {
        return this.$q((resolve, reject) => {
            this.getCollection(inst).then((collectionTree) => {
                if (pid) {
                    resolve(this.searchCollectionTree(collectionTree, pid, {value: 0, path: [], level: 0}, 0));
                }
            });
        });
    }

    getSubColThatHasItems(inst ?: string) {
        return this.$q((resolve, reject) => {
            this.getCollection(inst).then((collectionTree) => {
                resolve(this.searchCollectionTree(collectionTree, undefined, {value: 0, path: [], level: 0}, 0, true));
            });
        });
    }

    //recursive search function for tree
    private searchCollectionTree(collectionTree, pid, obj, objLevel, isSearchForItems ?: boolean): { value } {
        if (collectionTree.length === undefined) {//not the first call
            if ((isSearchForItems && collectionTree.items === 'Y') || (!isSearchForItems && collectionTree.pid.value == pid)) {// stop condition
                // build the object and return it:
                obj.value = collectionTree;
                return obj;
            }

            obj.path.push({name: collectionTree.name, pid: collectionTree.pid.value, level: objLevel});
            //	if (json.collections === undefined & !hasMoreSiblings) {// if is a leaf and has no siblings afterwards
            if (collectionTree.collections) {
                collectionTree = collectionTree.collections.collection;//update the json to be its children in order to search them
                if (getBackendSystem() === 'suprima' && collectionTree.length === undefined) {//in suprima if there is a single coll there is no array
                    collectionTree = [collectionTree];
                }
            }
        }

        if (collectionTree.length !== undefined) {//json is an array - not else since json is changed within the block above
            let objL = ++obj.level;
            for (let i = 0; i < collectionTree.length; i++) {

                let updatedObj = this.searchCollectionTree(collectionTree[i], pid, obj, objL, isSearchForItems);

                if (updatedObj === undefined || updatedObj.value === undefined) {//not found - remove from this level and down
                    obj.path = obj.path.filter(function (el) {
                        return el.level < obj.level;
                    });
                    obj.level = (i + 1 === collectionTree.length) ? obj.level - 1 : obj.level;
                }
                if ((updatedObj !== undefined)) {
                    obj.value = updatedObj.value;
                    return obj;
                }
            }
        }
    }

    public getItemsForCollection(collectionId: string, inst ?: string, offset ?: number, bulkSize ?: number, searchQuery ?: string, sortBy ?: string): Promise<any> {
        let institution = inst || getInstitutionCode();
        let q = "";
        searchQuery = searchQuery && searchQuery.endsWith(' ') ? searchQuery.trimEnd() : searchQuery ;

        q += 'cdparentid,exact,' + collectionId;
        if (searchQuery) {
            q += ',AND;' + searchQuery.split(';').join(' ').split('*').join(' ');
        }
        let args = {
            query: q,
            scope: getBackendSystem() !== 'suprima' ? 'collection_discovery_search' : 'browse_search',
            offset: offset | 0,
            bulkSize: bulkSize || this.resultUtil.updatedBulkSize,
            overrideViewInstitution: institution,
            sortby: sortBy,
            skipDelivery: !getPrimoVeSkipDeliveryForCollectionDiscovery() ? 'N' : 'Y',
            isCDSearch: true
        };
        if (searchQuery) {
            args.mode = 'advanced';
        }
        this.searchStateService.setOffsetLimitNotificationVisible(false);
        return this.cheetah.performSearch(args).catch(error => {
            if (this.restrictedOffsetHandlerService.isRestrictedOffsetError(error.data)) {
                return this.restrictedOffsetHandlerService.handleRestrictedOffsetError().then(result => {
                   switch (result) {
                       case HandleLimitOffsetErrorResult.RETRY:
                       case HandleLimitOffsetErrorResult.USER_LOGGED_IN:
                            return this.cheetah.performSearch(args);
                       case HandleLimitOffsetErrorResult.ERROR:
                           this.searchStateService.setOffsetLimitNotificationVisible(true);
                           throw error;
                   }
                });
            } else {
                throw error;
            }
        });
    }

    public getItemsForAllCollections(inst ?: string, offset ?: number, bulkSize ?: number, searchQuery ?: string, sortBy ?: string): Promise<any> {
        return this.getItemsForCollection('all_collections_search',inst,offset,bulkSize,searchQuery,sortBy);
    }

    public getSubcollectionsForSearchQuery(searchQuery: string, subcollections: [ICDCollectionObject], inst ?: string) {
        let institution = inst || getInstitutionCode();
        let subcollectionIds: string[] = [];
        let promises = [];
        return this.$q((resolve, reject) => {
            if (subcollections) {
                this.getSourceCodeIdByCollections(institution).then((sourceCode) => {
                    subcollectionIds = subcollections.map(c => getBackendSystem() !== 'suprima' ? sourceCode + c.pid.value : c.mms_id);
                    promises = this.prepareSearchRequests(searchQuery, subcollectionIds, institution);
                    resolve(promises);
                });
            } else {
                resolve(promises);
            }
        });
    }

    private prepareSearchRequests(searchQuery: string, subcollectionIds: string[], institution: string) {
        let promises = [];
        let chunkIdsNum: number = 970 - (searchQuery ? searchQuery.length : 0); //need to calculate query size since the maximum is 1000 (975 without constants).
        let chunkSize = 30;
        if (getBackendSystem() !== 'suprima') {
            if (subcollectionIds.length > 0) {
                let idsSizes = subcollectionIds.map(i => i.length);
                let maxIdSize = max(idsSizes) + 6; // more 6 for " OR " and scopes
                chunkIdsNum /= maxIdSize;
                chunkSize = Math.min(chunkSize, Math.floor(chunkIdsNum));
            }
        } else {
            if (subcollectionIds.length > 0) {
                chunkSize = Math.min(subcollectionIds.length, 100);
            }
        }
        let subcollectionIdsChunks = chunk(subcollectionIds, chunkSize);
        subcollectionIdsChunks.forEach(group => {
            let q: string = "";
            q += getBackendSystem() !== 'suprima' ? "rid" : "mms_id";
            q += ",contains,";
            group.forEach(e => q += e + ' OR ');
            q = q.substring(0, q.length - 4);
            if (searchQuery) {
                q += ',AND;' + searchQuery.split(';').join(' ').split('*').join(' ');
            }
            let args = {
                query: q,
                scope: getBackendSystem() !== 'suprima' ? 'collection_discovery_search' : 'browse_search',
                mode: 'advanced',
                bulkSize: chunkSize,
                overrideViewInstitution: institution
            };
            promises.push(this.cheetah.performSearch(args));
        });
        return promises;
    }

    public getDataFromCollectionRecord(record: PrimoResponse.IDoc): { type, collectionId, institution } {
        let collectionDiscoveryControlFields = get(record, 'pnx.control.colldiscovery');
        if (collectionDiscoveryControlFields) {
            return collectionDiscoveryControlFields.map((controlField) => {
                let dataMap;
                if (controlField) {
                    dataMap = {};
                    let splittedArr = controlField.split('$$');
                    for (let s of splittedArr) {
                        let data = s.substring(1);
                        switch (s.charAt(0)) {
                            case 'T':
                                dataMap['type'] = data.toUpperCase();
                                break;
                            case 'D':
                                dataMap['collectionId'] = data;
                                break;
                            case 'I':
                                dataMap['institution'] = data;
                                break;
                        }
                    }
                }
                return dataMap;
            });
        }
    }

    public getSourceCodeIdByCollections(inst ?: string): ng.IPromise<string> {
        let institution = inst || getInstitutionCode();
        if (this.sourceCodeIdPromiseMap[institution]) {
            return this.sourceCodeIdPromiseMap[institution];
        }
        let promise: ng.IPromise<string> = this.$q((resolve, reject) => {
            if (getBackendSystem() !== 'suprima') {
                let args = {
                    query: 'rtype,exact,collection',
                    scope: getBackendSystem() !== 'suprima' ? 'collection_discovery_search' : 'browse_search',
                    mode: 'advanced',
                    bulkSize: 1,
                    overrideViewInstitution: institution
                };
                this.cheetah.performSearch(args).then((data) => {
                    let docs = get(data, 'data.docs', {});
                    let sourceId = get(docs, '[0].pnx.control.sourceid[0]', '');
                    resolve(sourceId);
                });
            } else {
                resolve('');
            }
        });
        this.sourceCodeIdPromiseMap[institution] = promise;
        return promise;
    }

    public getCollectionRecordByCId(collectionId: string, inst ?: string): Promise<PrimoResponse.IDoc> {
        let institution = inst || getInstitutionCode();
        return this.$q((resolve, reject) => {
            this.getSourceCodeIdByCollections(institution).then((sourceCode) => {
                this.getRecordByDocId((getBackendSystem() !== 'suprima') ? sourceCode + collectionId : '' + collectionId, institution).then((record) => {
                    resolve(record);
                });
            });
        })
    }

    public getRecordByDocId(docId: string, inst ?: string) {
        let institution = inst || getInstitutionCode();
        let args = {
            query: 'any,exact,' + docId,
            scope: getBackendSystem() !== 'suprima' ? 'collection_discovery_search' : 'browse_search',
            overrideViewInstitution: institution
        };
        return this.cheetah.performSearch(args).then((data) => {
            return get(data, 'data.docs[0]');
        });
    }

    public getThumbnailFromItem(item: PrimoResponse.IDoc) {
        let linkArr = get(item, 'delivery.link', []);
        let thumbnailLink = find(linkArr, (link) => {
            return link.linkType.indexOf('thumbnail') > -1 && link.linkURL
        });
        return get(thumbnailLink, 'linkURL');
    }

    set itemsViewStyle(style: string) {
        this._itemsViewStyle = style;
    }

    get itemsViewStyle() {
        return this._itemsViewStyle;
    }

    get isCollectionDiscoverySpecificDisplay() {
        return this._isCollectionDiscoverySpecificDisplay;
    }
}

export interface ICDCollectionObject {
    collections: { collection: [ICDCollectionObject] },
    description: string,
    items: string,
    library: object,
    pid: { link: string, value: string },
    thumbnail: string,
    name: string,
    title?: string
}


export interface ICollectionPathObject {
    level: number,
    name: string,
    pid: string
}


// WEBPACK FOOTER //
// ./src/main/webapp/components/collectionDiscovery/collectionDiscovery.service.ts